// @ts-nocheck
import React, { useEffect, useState, useContext } from "react";
import appConst from "../Utilities/appConst.json";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchShippingMethodsData,
  shippingActions,
} from "../store/shippingSlice";
import { alertActions } from "../store/alertSlice";
import ShippingMethodAccordion from "../Components/ShippingMethodAccordion";
import { LanguageContext } from "../context/language/LanguageContext";
import { footerActions } from "../store/footerSlice";
import { CircleAlert } from "lucide-react";
import { loaderActions } from "../store/loaderSlice";
import { basketActions } from "../store/basket";
import { disableButton, enableButton } from "../Utilities/styleUtil";
import { fetchAllShopsCities, shopsActions } from "../store/shopsSlice";
import { postCouponInput } from "../store/couponsSlice";
import { useHistory, useLocation } from "react-router-dom";
import { couponsActions } from "../store/couponsSlice";
import { getCoupons } from "../store/couponsSlice";
import { Tabs, TabsList, TabsTrigger, TabContent } from "../Components/ui";
import starsIcon from "../img/stars.svg";
import type { ShippingOption } from "../Types/shipping";
import type { ShippingPageState } from "../Types/shippingPageState";
import PageLayout from "../Components/PageLayout";

const SHIPPING_TAB_REGULAR = "regular";
const SHIPPING_TAB_EXPEDITED = "expedited";
const EXPEDITED_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
type ShippingSelectionPayload = { selectedId: string | number };
type SelectedShippingMethodPayload = {
  selectedShippingMethod: ShippingOption | null;
};
type BasketDeliveryTypePayload = { id: string | number };
type BasketDeliveryShopPayload = {
  shop: { city: string; site: string; site_id: string | number } | null;
};

const isExpeditedShippingMethod = (method: ShippingOption | null | undefined) =>
  Boolean(method?.isExpedited);

const getShippingMethodKey = (
  method: ShippingOption | null | undefined
): string | null => {
  if (!method || method.id == null) {
    return null;
  }

  return `${method.id}-${isExpeditedShippingMethod(method) ? "expedited" : "regular"}`;
};

const findShippingMethodByKey = (
  methods: ShippingOption[],
  key: string | null
): ShippingOption | null => {
  if (!key) {
    return null;
  }

  return methods.find((method) => getShippingMethodKey(method) === key) || null;
};

const formatCountdown = (timeRemainingMs: number) => {
  const totalSeconds = Math.max(0, Math.ceil(timeRemainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const Shipping = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const languageContext = useContext(LanguageContext);

  const shippingMethods = useSelector(
    (state: ShippingPageState) => state.shipping.shippingMethods
  );
  const footer = useSelector((state: ShippingPageState) => state.footer.summary);
  const selectedIdFromStore = useSelector(
    (state: ShippingPageState) => state.shipping.selectedId
  );
  const selectedShippingMethodFromStore = useSelector(
    (state: ShippingPageState) => state.shipping.selectedShippingMethod
  );
  const isValid = useSelector((state: ShippingPageState) => state.shops.isValid);
  const city = useSelector((state: ShippingPageState) => state.shops.city);
  const site = useSelector((state: ShippingPageState) => state.shops.site);
  const siteId = useSelector((state: ShippingPageState) => state.shops.siteId);
  const isNewsletter = useSelector(
    (state: ShippingPageState) => state.user.isNewsletter
  );
  const basket = useSelector((state: ShippingPageState) => state.basket.basket);
  const isCollectionBucketCouponFromShipping = useSelector(
    (state: ShippingPageState) => state.coupons.isCollectionBucketCouponFromShipping
  );
  const isCollectionBucket = useSelector(
    (state: ShippingPageState) => state.user.isCollectionBucket
  );
  const direction = useSelector((state: ShippingPageState) => state.mode.direction);
  const [wasSelectionMade, setWasSelectionMade] = useState(false);
  const [selectedId, setSelectedId] = useState<string | number>(-1);
  const [selectedShippingKey, setSelectedShippingKey] = useState<string | null>(
    null
  );
  const [shippingMethod, setShippingMethod] = useState<ShippingOption | null>(null);
  const [coupon, setCoupon] = useState<string | null>(null);
  const [single, setSingle] = useState(true);
  const [shippingTab, setShippingTab] = useState(SHIPPING_TAB_REGULAR);
  const [hasHydratedShippingState, setHasHydratedShippingState] = useState(false);
  const [expeditedRefreshAt, setExpeditedRefreshAt] = useState<number | null>(
    null
  );
  const [expeditedTimeRemainingMs, setExpeditedTimeRemainingMs] = useState(
    EXPEDITED_REFRESH_INTERVAL_MS
  );

  const product = sessionStorage.getItem(appConst.PRODUCT);
  const regularShippingMethods = (shippingMethods || []).filter(
    (method: ShippingOption) => !isExpeditedShippingMethod(method)
  );
  const expeditedShippingMethods = (shippingMethods || []).filter(
    (method: ShippingOption) => isExpeditedShippingMethod(method)
  );
  const hasRegularShippingMethods = regularShippingMethods.length > 0;
  const hasExpeditedShippingMethods = expeditedShippingMethods.length > 0;
  const filteredShippingMethods =
    shippingTab === SHIPPING_TAB_EXPEDITED
      ? expeditedShippingMethods
      : regularShippingMethods;
  const shouldShowExpeditedRefreshCounter =
    shippingTab === SHIPPING_TAB_EXPEDITED &&
    expeditedShippingMethods.length > 0 &&
    expeditedRefreshAt !== null;

  useEffect(() => {
    dispatch(basketActions.setShippingAlertVisible(true));
    dispatch(footerActions.setIsShow(true));
    dispatch(
      footerActions.setBtnText(
        languageContext.getTranslation(appConst.TO_SEVENTH_PAGE)
      )
    );
    dispatch(footerActions.setNextPage(""));
    dispatch(footerActions.setBeforeContinue(beforeContinue));

    dispatch(shopsActions.setIsFormSubmited({ isFormSubmited: false }));
    dispatch(alertActions.closeAlert());
    dispatch(couponsActions.setIsCollectionBucketCouponFromShipping(false));
    dispatch(fetchAllShopsCities());
    updateFooter();

    return () => {
      dispatch(basketActions.setShippingAlertVisible(false));
    };
  }, []);

  useEffect(() => {
    switch (selectedId) {
      case -1:
        dispatch(footerActions.setNextPage(""));
        break;
      case parseInt(appConst.BHSHOPS, 10):
        dispatch(
          footerActions.setNextPage(isValid ? appConst.SEVENTH_PAGE : "")
        );
        if (!isValid) {
          disableButton(dispatch);
        }
        break;
      default:
        dispatch(footerActions.setNextPage(appConst.SEVENTH_PAGE));
    }
    updateFooter();
    dispatch(footerActions.setBeforeContinue(beforeContinue));
  }, [selectedId, isValid]);

  useEffect(() => {
    if (isCollectionBucketCouponFromShipping) {
      // If not from back history or if from back history after collection bucket coupon entry
      if (history.action !== "POP" || (!single && history.action === "POP")) {
        history.push({
          pathname: appConst.SEVENTH_PAGE,
          search: location.search,
        });
      }

      setWasSelectionMade(false);
    }
    setSingle(false);
  }, [isCollectionBucketCouponFromShipping]);

  useEffect(() => {
    if (
      (wasSelectionMade && selectedId != appConst.BHSHOPS) ||
      (wasSelectionMade && selectedId == appConst.BHSHOPS && isValid)
    ) {
      enableButton(dispatch);
    } else {
      disableButton(dispatch);
    }
  }, [shippingMethod]);

  // For loader: should be befor useEffect(() => {...}, [dispatch])
  useEffect(() => {
    if (shippingMethods?.length > 0) {
      const availableShippingMethods = shippingMethods.filter(
        (method: ShippingOption) => method.isAvailable == true
      );
      if (availableShippingMethods.length < shippingMethods.length) {
        // dispatch(shippingActions.setShippingMethods(availableShippingMethods));
      }

      dispatch(loaderActions.loaderHide());

      const selectedShippingMethod = findShippingMethodByKey(
        shippingMethods,
        selectedShippingKey
      );
      const storeShippingMethodKey = getShippingMethodKey(
        selectedShippingMethodFromStore
      );
      const initialShippingMethodFromStore = hasHydratedShippingState
        ? null
        : findShippingMethodByKey(shippingMethods, storeShippingMethodKey) ||
          shippingMethods.find(
            (shippingMethod: ShippingOption) => shippingMethod.id === selectedIdFromStore
          );

      if (selectedShippingMethod) {
        setShippingTab(
          isExpeditedShippingMethod(selectedShippingMethod)
            ? SHIPPING_TAB_EXPEDITED
            : SHIPPING_TAB_REGULAR
        );
        setShippingMethod(selectedShippingMethod);
        setSelectedId(selectedShippingMethod.id);
        setSelectedShippingKey(getShippingMethodKey(selectedShippingMethod));
        setWasSelectionMade(true);
      } else if (initialShippingMethodFromStore) {
        setShippingTab(
          isExpeditedShippingMethod(initialShippingMethodFromStore)
            ? SHIPPING_TAB_EXPEDITED
            : SHIPPING_TAB_REGULAR
        );
        setShippingMethod(initialShippingMethodFromStore);
        setSelectedId(initialShippingMethodFromStore.id);
        setSelectedShippingKey(getShippingMethodKey(initialShippingMethodFromStore));
        setWasSelectionMade(true);
      } else {
        const nextShippingTab =
          shippingTab === SHIPPING_TAB_EXPEDITED &&
          expeditedShippingMethods.length > 0
            ? SHIPPING_TAB_EXPEDITED
            : regularShippingMethods.length > 0
              ? SHIPPING_TAB_REGULAR
              : SHIPPING_TAB_EXPEDITED;

        setShippingTab(nextShippingTab);
        setShippingMethod(null);
        setSelectedId(-1);
        setSelectedShippingKey(null);
        setWasSelectionMade(false);
      }
      setHasHydratedShippingState(true);
    } else {
      setShippingTab(SHIPPING_TAB_REGULAR);
      setShippingMethod(null);
      setSelectedId(-1);
      setSelectedShippingKey(null);
      setWasSelectionMade(false);
      setHasHydratedShippingState(false);
    }
    dispatch(footerActions.setBeforeContinue(beforeContinue));
  }, [shippingMethods]);

  useEffect(() => {
    if (shippingMethods && shippingMethods.length > 0) {
      const storeShippingMethod =
        findShippingMethodByKey(
          shippingMethods,
          getShippingMethodKey(selectedShippingMethodFromStore)
        ) ||
        shippingMethods.find(
          (shippingMethod: ShippingOption) => shippingMethod.id === selectedIdFromStore
        );

      setShippingMethod(storeShippingMethod || null);
      setSelectedId(storeShippingMethod ? storeShippingMethod.id : -1);
      setSelectedShippingKey(getShippingMethodKey(storeShippingMethod));
      setWasSelectionMade(Boolean(storeShippingMethod));
    }

    dispatch(footerActions.setBeforeContinue(beforeContinue));
  }, [selectedIdFromStore, selectedShippingMethodFromStore]);

  useEffect(() => {
    if (coupon == null) return;
    dispatch(loaderActions.loaderShow());
    dispatch(
      postCouponInput(
        coupon,
        basket,
        true // set isCollectionBucketCouponFromShipping to true, because came from shipping page
      )
    );
    setCoupon(null);
  }, [coupon]);

  // For loader: should be after useEffect(() => {...}, [shippingMethods])
  useEffect(() => {
    if (shippingMethods && shippingMethods.length > 0) {
      dispatch(loaderActions.loaderHide());
    } else {
      dispatch(loaderActions.loaderShow());
    }
  }, [dispatch]);

  useEffect(() => {
    if (shippingTab !== SHIPPING_TAB_EXPEDITED || !basket) {
      setExpeditedRefreshAt(null);
      setExpeditedTimeRemainingMs(EXPEDITED_REFRESH_INTERVAL_MS);
      return;
    }

    let isCancelled = false;
    let refreshTimeoutId: number | null = null;
    let isRefreshing = false;

    const refreshExpeditedMethods = async () => {
      if (isCancelled || isRefreshing) {
        return;
      }

      isRefreshing = true;
      const nextRefreshTime = Date.now() + EXPEDITED_REFRESH_INTERVAL_MS;

      setExpeditedRefreshAt(nextRefreshTime);
      setExpeditedTimeRemainingMs(EXPEDITED_REFRESH_INTERVAL_MS);

      try {
        await dispatch(fetchShippingMethodsData(basket));
      } finally {
        isRefreshing = false;

        if (!isCancelled) {
          refreshTimeoutId = window.setTimeout(
            refreshExpeditedMethods,
            Math.max(nextRefreshTime - Date.now(), 0)
          );
        }
      }
    };

    refreshExpeditedMethods();

    return () => {
      isCancelled = true;
      if (refreshTimeoutId !== null) {
        window.clearTimeout(refreshTimeoutId);
      }
    };
  }, [basket, dispatch, shippingTab]);

  useEffect(() => {
    if (shippingTab !== SHIPPING_TAB_EXPEDITED || expeditedRefreshAt === null) {
      return;
    }

    const updateCountdown = () => {
      setExpeditedTimeRemainingMs(
        Math.max(expeditedRefreshAt - Date.now(), 0)
      );
    };

    updateCountdown();

    const countdownIntervalId = window.setInterval(updateCountdown, 1000);

    return () => {
      window.clearInterval(countdownIntervalId);
    };
  }, [expeditedRefreshAt, shippingTab]);

  const updateShipping = (
    selectedId: ShippingSelectionPayload,
    selectedShippingMethod: SelectedShippingMethodPayload
  ) => {
    dispatch(shippingActions.setSelectedId(selectedId));
    dispatch(shippingActions.setSelectedShippingMethod(selectedShippingMethod));
  };

  const updateBasket = (
    id: BasketDeliveryTypePayload,
    shop: BasketDeliveryShopPayload
  ) => {
    dispatch(basketActions.setDeliveryDetailsType(id));
    dispatch(basketActions.setDeliveryDetailsShop(shop));
  };

  const updateFooter = () => {
    if (wasSelectionMade && shippingMethod) {
      // the shipping methods could be updated if we go back and change the basket
      const shippingDiscount = isNewsletter
        ? shippingMethod.price - shippingMethod.priceForClubMember
        : 0;
      const totalWithShipping =
        footer.totalwithoutShipping + shippingMethod.price - shippingDiscount;
      dispatch(
        footerActions.setTempFooterFromShipping({
          tempShippingDiscount: shippingDiscount,
          tempAdditionalDiscount: footer.additionalDiscount,
          tempTotal: totalWithShipping,
          shipping: shippingMethod.price,
        })
      );
    } else {
      dispatch(
        footerActions.setTempFooterFromShipping({
          tempShippingDiscount: null,
          tempAdditionalDiscount: footer.additionalDiscount
            ? footer.additionalDiscount
            : null,
          tempTotal: footer.totalwithoutShipping,
          shipping: null,
        })
      );
    }
    dispatch(footerActions.setBeforeContinue(beforeContinue));
  };

  const handleOnSelect = (nextShippingMethod: ShippingOption) => {
    const nextShippingKey = getShippingMethodKey(nextShippingMethod);
    const isAlreadySelected = nextShippingKey === selectedShippingKey;

    if (isAlreadySelected) {
      setWasSelectionMade(false);
      disableButton(dispatch);
      setSelectedId(-1);
      setSelectedShippingKey(null);
      setShippingMethod(null);
    } else {
      setWasSelectionMade(true);
      // if ((nextShippingMethod.id == appConst.BHSHOPS && isValid) || nextShippingMethod.id != appConst.BHSHOPS) {
      //   enableButton(dispatch);
      // }
      setSelectedId(nextShippingMethod.id);
      setSelectedShippingKey(nextShippingKey);
      setShippingMethod(nextShippingMethod);
    }
  };

  const handleTabChange = (tabValue: string) => {
    if (tabValue === shippingTab) {
      return;
    }

    if (
      (tabValue === SHIPPING_TAB_REGULAR && !hasRegularShippingMethods) ||
      (tabValue === SHIPPING_TAB_EXPEDITED && !hasExpeditedShippingMethods)
    ) {
      return;
    }

    setShippingTab(tabValue);
    setWasSelectionMade(false);
    setSelectedId(-1);
    setSelectedShippingKey(null);
    setShippingMethod(null);
    disableButton(dispatch);
    updateShipping({ selectedId: -1 }, { selectedShippingMethod: null });
    updateBasket({ id: -1 }, { shop: null });
  };

  useEffect(() => {
    updateBasket({ id: selectedId }, { shop: { city, site, site_id: siteId } });
  }, [city, site, siteId]);

  const beforeContinue = () => {
    dispatch(
      shopsActions.setIsFormSubmited({
        isFormSubmited: true,
      })
    );

    // If no shipping method is selected - alert about it and reset
    if (!wasSelectionMade) {
      dispatch(
        alertActions.createAlert({
          title: languageContext.getTranslation(appConst.ALERT_SHIPPING_TITLE),
          message: languageContext.getTranslation(
            appConst.ALERT_SHIPPING_MESSAGE
          ),
          isDialog: false,
        })
      );
      updateShipping({ selectedId: -1 }, { selectedShippingMethod: null });
      updateBasket({ id: -1 }, { shop: null });
    }
    // If the shipping method that selected is SHOPS - set it on store includes the delivery details shop
    else if (selectedId == appConst.BHSHOPS && isValid) {
      updateShipping(
        { selectedId: selectedId },
        { selectedShippingMethod: shippingMethod }
      );
      dispatch(footerActions.setFooterFromShipping());

      if (!isCollectionBucketCouponFromShipping) {
        dispatch(getCoupons([shippingMethod]));
      }
    }
    // If the shipping method that selected is not SHOPS - set it on store includes and reset the delivery details shop
    else if (selectedId != appConst.BHSHOPS) {
      updateShipping(
        { selectedId: selectedId },
        { selectedShippingMethod: shippingMethod }
      );
      updateBasket({ id: selectedId }, { shop: null });
      dispatch(footerActions.setFooterFromShipping());

      if (!isCollectionBucketCouponFromShipping) {
        dispatch(getCoupons([shippingMethod]));
      }
    }
  };

  const alertSelectedYes = (value: string | null) => {
    setCoupon(value);
  };

  const openCollectionBucketAlert = () => {
    dispatch(
      alertActions.createAlert({
        title: languageContext.getTranslation(
          appConst.ALERT_COLLECTION_BUCKET_TITLE
        ),
        message: languageContext.getTranslation(
          appConst.ALERT_COLLECTION_BUCKET_MESSAGE
        ),
        funcYes: (value: string | null) => alertSelectedYes(value),
        yesButtonText: appConst.APPROVE,
        noButtonText: appConst.CANCEL,
        isInput: true,
        isDialog: true,
      })
    );
  };

  const [unionAlertFirstLine, unionAlertSecondLine] = languageContext
    .getTranslation(appConst.ALERT_CAN_NOT_UNION_ORDERS)
    .split("\n");

  return (
    <PageLayout className="shipping_page" data-testid="shipping-page">
      <div className="flex flex-col gap-7">
        <div
          dir={direction}
          className="shipping-page-union-alert bg-lupa-divider"
          data-testid="shipping-union-orders-alert"
        >
          <div className="flex flex-col items-start justify-center gap-1 px-3 py-2 text-start text-xs text-secondary-foreground md:px-0 md:text-base">
            <div className="flex flex-row items-center justify-start gap-1">
              <CircleAlert className="h-4 w-4 shrink-0 text-secondary-foreground" />
              {unionAlertFirstLine}
            </div>
            {unionAlertSecondLine && (
              <div className="flex flex-row items-center justify-start gap-1">
                <CircleAlert className="h-4 w-4 shrink-0 text-secondary-foreground" />
                {unionAlertSecondLine}
              </div>
            )}
          </div>
        </div>

        <div className="flex w-full flex-col gap-7">
          {shippingMethods &&
            shippingMethods.length > 0 &&
            hasExpeditedShippingMethods && (
            <div
              className="mx-3 flex flex-col gap-3 md:mx-0"
              data-testid="shipping-tabs-section"
            >
              <Tabs
                value={shippingTab}
                onValueChange={handleTabChange}
                dir={direction}
                data-testid="shipping-tabs"
              >
                <TabsList size="small" data-testid="shipping-tabs-list">
                  {hasRegularShippingMethods && (
                    <TabsTrigger
                      size="small"
                      value={SHIPPING_TAB_REGULAR}
                      className="text-sm md:text-lg"
                      data-testid="shipping-tab-regular"
                    >
                      <TabContent>
                        {languageContext.getTranslation(appConst.SHIPPING)}
                      </TabContent>
                    </TabsTrigger>
                  )}
                  {hasExpeditedShippingMethods && (
                    <TabsTrigger
                      size="small"
                      value={SHIPPING_TAB_EXPEDITED}
                      className="text-sm md:text-lg"
                      data-testid="shipping-tab-expedited"
                    >
                      <TabContent
                        iconPosition="start"
                        icon={
                          <img
                            src={starsIcon}
                            alt=""
                            aria-hidden
                            className="block h-4 w-4 self-center"
                          />
                        }
                      >
                        {languageContext.getTranslation(appConst.EXPEDITED_SHIPPING)}
                      </TabContent>
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>

              {shouldShowExpeditedRefreshCounter && (
                <div
                  className="inline-flex h-[26px] w-[74px] items-center justify-center rounded-[20px] bg-lupa-highlight px-3 py-1 font-ploni text-sm font-normal leading-none tabular-nums text-secondary-foreground"
                  data-testid="shipping-expedited-countdown"
                >
                  {formatCountdown(expeditedTimeRemainingMs)}
                </div>
              )}
            </div>
            )}

          <div
            className="flex flex-col gap-3 md:gap-6"
            data-testid="shipping-methods-list"
          >
            {filteredShippingMethods &&
              filteredShippingMethods.length > 0 &&
              filteredShippingMethods.map((option: ShippingOption, index: number) => (
                <ShippingMethodAccordion
                  index={index}
                  onChange={null}
                  onClick={handleOnSelect}
                  expandedPanel={
                    selectedShippingKey === getShippingMethodKey(option)
                  }
                  wasSelectionMade={wasSelectionMade}
                  option={option}
                  key={"order_details_" + option.id + index}
                  isChecked={
                    selectedShippingKey === getShippingMethodKey(option)
                  }
                  product={product}
                  id={option.id}
                  testId={`shipping-method-${getShippingMethodKey(option) ?? option.id}`}
                />
              ))}

            {isCollectionBucket &&
              filteredShippingMethods &&
              filteredShippingMethods.length > 0 && (
              <div
                dir={direction}
                onClick={openCollectionBucketAlert}
                className="cursor-pointer text-start underline ms-3 md:ms-0"
                data-testid="shipping-collection-bucket-trigger"
              >
                <p>
                  {languageContext.getTranslation(
                    appConst.COLLECTION_BUCKET_BUTTON
                  )}
                </p>
              </div>
              )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
export default Shipping;
